// Canonical stage → color mapping for the members surface. Single source of
// truth so the StageBadge here AND the future lifecycle stacked-area chart
// (TASK-033) share one palette.
//
// Color intent matches PRD §6.3.2 interpretation guidance: NEW + ENGAGED
// are positive (green/blue), ACTIVE is steady (sky), DORMANT is neutral
// (slate), AT_RISK + CHURNED are warning/danger (amber/rose).

import type { MemberLifecycleStage } from "@community-analytics/shared";

export interface StageStyle {
  /** Tailwind text-color class for the badge label. */
  textClass: string;
  /** Tailwind background-color class for the badge pill. */
  backgroundClass: string;
  /** Tailwind border-color class for the badge outline. */
  borderClass: string;
  /** Hex color for charts (Recharts series fills). */
  hex: string;
}

export const STAGE_STYLES: Readonly<Record<MemberLifecycleStage, StageStyle>> = {
  NEW: {
    textClass: "text-emerald-700 dark:text-emerald-300",
    backgroundClass: "bg-emerald-100 dark:bg-emerald-950",
    borderClass: "border-emerald-300 dark:border-emerald-800",
    hex: "#10b981", // emerald-500
  },
  ACTIVE: {
    textClass: "text-sky-700 dark:text-sky-300",
    backgroundClass: "bg-sky-100 dark:bg-sky-950",
    borderClass: "border-sky-300 dark:border-sky-800",
    hex: "#0ea5e9", // sky-500
  },
  ENGAGED: {
    textClass: "text-violet-700 dark:text-violet-300",
    backgroundClass: "bg-violet-100 dark:bg-violet-950",
    borderClass: "border-violet-300 dark:border-violet-800",
    hex: "#8b5cf6", // violet-500
  },
  DORMANT: {
    textClass: "text-slate-700 dark:text-slate-300",
    backgroundClass: "bg-slate-100 dark:bg-slate-900",
    borderClass: "border-slate-300 dark:border-slate-700",
    hex: "#64748b", // slate-500
  },
  AT_RISK: {
    textClass: "text-amber-700 dark:text-amber-300",
    backgroundClass: "bg-amber-100 dark:bg-amber-950",
    borderClass: "border-amber-300 dark:border-amber-800",
    hex: "#f59e0b", // amber-500
  },
  CHURNED: {
    textClass: "text-rose-700 dark:text-rose-300",
    backgroundClass: "bg-rose-100 dark:bg-rose-950",
    borderClass: "border-rose-300 dark:border-rose-800",
    hex: "#f43f5e", // rose-500
  },
};

export function stageStyle(stage: MemberLifecycleStage): StageStyle {
  return STAGE_STYLES[stage];
}
